// @greenlight-spec: 28-it-dashboard-ui.md#internal-health--internal-health-org-admin
// @greenlight-spec: 11-observability.md#metric-history
import * as React from 'react';

import {
  APP_CAPACITY_CRITICAL_UTILIZATION,
  QUEUE_BACKLOG_ALERT_SECONDS,
  type HealthSeries,
} from '@greenlight/shared';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { PERF_TARGETS } from '@/pages/internal-health/perfTargets';

import { HistoryChart, type HistoryChartProps, type HistoryLine } from './HistoryChart';
import {
  errorRate,
  noLimitMessage,
  someWithoutLimit,
  scopeFloor,
  scopePeak,
  scopePercent,
  scopeValues,
  stepMsOf,
  sumAcrossScopes,
  tighterOf,
  worstScopePercent,
  type HistoryMarker,
  type HistoryPoint,
} from './historyModel';
import { useHistory } from './HistoryContext';
import { CLUSTER_SERIES, OTHER_SERIES_COLOR, seriesColors } from '@/components/health/seriesColors';
import { useSeries, type SeriesResult } from './useHealthHistory';

const percent = (v: number): string => `${Math.round(v)}%`;
const rate = (v: number): string => `${v.toFixed(v < 10 ? 1 : 0)}%`;
const count = (v: number): string => String(Math.round(v));
const seconds = (v: number): string => `${Math.round(v)} s`;
const ms = (v: number): string => `${Math.round(v)} ms`;

interface ChartContext {
  markers: HistoryMarker[];
}

type Presentation = Omit<
  HistoryChartProps,
  'points' | 'lines' | 'stepMs' | 'markers' | 'refreshing' | 'testId'
>;

function sameWindow(series: HealthSeries[]): boolean {
  const [first] = series;
  return series.every(
    (s) => s.from === first!.from && s.to === first!.to && s.step === first!.step,
  );
}

function sameSeries(a: HealthSeries[], b: HealthSeries[] | null): boolean {
  return b !== null && a.length === b.length && a.every((s, i) => s === b[i]);
}

/** Loading, error, then the chart: every chart on the section goes through here. */
function SeriesChart({
  results,
  toPoints,
  presentation,
  context,
  testId,
}: {
  results: SeriesResult[];
  toPoints: (series: HealthSeries[]) => {
    points: HistoryPoint[];
    reference?: HistoryChartProps['reference'];
    domainMax?: number;
    emptyMessage?: string;
    note?: string;
    lines?: HistoryLine[];
  };
  presentation: Presentation;
  context: ChartContext;
  testId: string;
}): React.ReactElement {
  const ready = results.map((r) => r.data).filter((d): d is HealthSeries => d !== undefined);
  const current = ready.length === results.length && sameWindow(ready) ? ready : null;
  // A range change can settle one series before another; until they cover the
  // same window, keep drawing the last set that did rather than pairing them.
  const [held, setHeld] = React.useState<HealthSeries[] | null>(current);
  if (current && !sameSeries(current, held)) setHeld(current);
  const shown = current ?? held;
  if (!shown || ready.length < results.length) {
    if (results.some((r) => r.isError)) {
      return (
        <Alert variant="destructive" className="py-2" data-testid={`${testId}-error`}>
          <AlertDescription>
            Couldn&rsquo;t load {presentation.title.toLowerCase()} history.
          </AlertDescription>
        </Alert>
      );
    }
    return <Skeleton className="h-40 w-full rounded-gl-icon" data-testid={`${testId}-loading`} />;
  }
  const shaped = toPoints(shown);
  const omitted = Math.max(...shown.map((series) => series.omitted_scopes));
  const notes = [
    omitted > 0
      ? `${omitted} older ${omitted === 1 ? 'replica is' : 'replicas are'} not shown, so earlier intervals may be incomplete.`
      : null,
    shaped.emptyMessage ? null : (shaped.note ?? null),
  ].filter((note): note is string => note !== null);
  return (
    <HistoryChart
      {...presentation}
      {...(shaped.reference ? { reference: shaped.reference } : {})}
      {...(shaped.domainMax !== undefined ? { domainMax: shaped.domainMax } : {})}
      {...(shaped.emptyMessage ? { emptyMessage: shaped.emptyMessage } : {})}
      {...(notes.length > 0 ? { note: notes.join(' ') } : {})}
      {...(shaped.lines && shaped.lines.length > 0 ? { lines: shaped.lines } : {})}
      points={shaped.points}
      stepMs={stepMsOf(shown[0]!.step)}
      markers={context.markers}
      refreshing={current === null || results.some((r) => r.refreshing)}
      testId={testId}
    />
  );
}

/**
 * A line per replica that drew anything, keyed to the dots beside the replicas
 * the card lists; a replica the card no longer lists is drawn without a hue.
 */
function replicaLines(
  series: HealthSeries,
  colors: ReadonlyMap<string, string>,
): HistoryLine[] | undefined {
  if (colors.size === 0) return undefined;
  const lines = series.scopes.flatMap((s) => {
    if (s.scope === null) return [];
    const points = scopePercent(series, s.scope);
    if (!points.some((p) => p.peak !== null)) return [];
    return [
      { key: s.scope, label: s.scope, color: colors.get(s.scope) ?? OTHER_SERIES_COLOR, points },
    ];
  });
  const keyed = (line: HistoryLine): number => (line.color === OTHER_SERIES_COLOR ? 1 : 0);
  return lines.sort((a, b) => keyed(a) - keyed(b) || a.key.localeCompare(b.key));
}

/**
 * One service's memory, CPU, restarts and ready replicas, read for that service
 * alone, so each chart describes the service whose block it sits in. With two
 * or more replicas, memory and CPU draw each replica as its own line.
 */
export function ServiceCharts({
  replicas = [],
}: {
  /** The replicas the card lists now, whose dots key the lines. */
  replicas?: readonly string[];
}): React.ReactElement {
  const context = useHistory();
  const colors = seriesColors(replicas);
  const perReplica = colors.size > 0;
  const ofEach = perReplica ? 'Each replica' : 'Highest replica';
  const otherLinesLabel = 'Other replicas';
  const { service } = context;
  const memory = useSeries('control_plane_memory', context.span, service);
  const cpu = useSeries('control_plane_cpu', context.span, service);
  const restarts = useSeries('control_plane_restart_delta', context.span, service);
  const ready = useSeries('availability_ready_replicas', context.span, service);
  const critical = {
    value: APP_CAPACITY_CRITICAL_UTILIZATION,
    label: `${APP_CAPACITY_CRITICAL_UTILIZATION}% critical`,
  };
  const ofLimit = (s: HealthSeries) => {
    const points = worstScopePercent(s);
    return {
      points,
      domainMax: 100,
      lines: replicaLines(s, colors),
      emptyMessage: noLimitMessage(s, points),
      ...(someWithoutLimit(s) ? { note: 'Readings with no limit set are not drawn.' } : {}),
    };
  };
  return (
    <>
      <SeriesChart
        testId="history-memory"
        results={[memory]}
        toPoints={([s]) => ({ ...ofLimit(s!), reference: critical })}
        presentation={{
          title: 'Memory',
          caption: `${ofEach}, % of its own limit`,
          format: percent,
          otherLinesLabel,
        }}
        context={context}
      />
      <SeriesChart
        testId="history-cpu"
        results={[cpu]}
        toPoints={([s]) => ofLimit(s!)}
        presentation={{
          title: 'CPU',
          caption: `${ofEach}, % of its own limit`,
          format: percent,
          otherLinesLabel,
        }}
        context={context}
      />
      <SeriesChart
        testId="history-restarts"
        results={[restarts]}
        toPoints={([s]) => ({ points: sumAcrossScopes(s!) })}
        presentation={{
          title: 'Restarts',
          caption: 'Container restarts observed, all its replicas',
          format: count,
          mark: 'columns',
        }}
        context={context}
      />
      <SeriesChart
        testId="history-ready"
        results={[ready]}
        toPoints={([s]) => {
          const { points, limit } = scopeFloor(s!, service ?? '');
          return limit === null
            ? { points }
            : {
                points,
                reference: { value: limit, label: `${limit} desired` },
                domainMax: limit,
              };
        }}
        presentation={{
          title: 'Replicas ready',
          caption: 'Fewest ready in each interval',
          format: count,
        }}
        context={context}
      />
    </>
  );
}

export function ClusterCharts(): React.ReactElement {
  const context = useHistory();
  const cpu = useSeries('cluster_cpu_requested', context.span);
  const memory = useSeries('cluster_memory_requested', context.span);
  return (
    <SeriesChart
      testId="history-cluster"
      results={[cpu, memory]}
      toPoints={([c, m]) => {
        const cpuPoints = worstScopePercent(c!);
        const memoryPoints = worstScopePercent(m!);
        return {
          points: tighterOf(cpuPoints, memoryPoints),
          lines: [
            { key: 'cpu', label: 'CPU', color: CLUSTER_SERIES.cpu, points: cpuPoints },
            { key: 'memory', label: 'Memory', color: CLUSTER_SERIES.memory, points: memoryPoints },
          ],
          reference: {
            value: APP_CAPACITY_CRITICAL_UTILIZATION,
            label: `${APP_CAPACITY_CRITICAL_UTILIZATION}% critical`,
          },
          domainMax: 100,
        };
      }}
      presentation={{
        title: 'Cluster capacity used',
        caption: 'CPU and memory reserved by pods, of what nodes offer',
        format: percent,
      }}
      context={context}
    />
  );
}

export function BackgroundWorkCharts(): React.ReactElement {
  const context = useHistory();
  const oldest = useSeries('queue_oldest_pending_age', context.span);
  return (
    <SeriesChart
      testId="history-queue"
      results={[oldest]}
      toPoints={([s]) => ({
        points: scopeValues(s!, null),
        reference: {
          value: QUEUE_BACKLOG_ALERT_SECONDS,
          label: `${QUEUE_BACKLOG_ALERT_SECONDS} s budget`,
        },
      })}
      presentation={{
        title: 'Oldest waiting job',
        caption: 'How long the oldest runnable job had waited',
        format: seconds,
      }}
      context={context}
    />
  );
}

const FAMILIES = [
  { scope: 'auth_check', title: 'App access check latency', target: PERF_TARGETS.authCheckMs },
  { scope: 'data_proxy', title: 'Data proxy latency', target: PERF_TARGETS.dataProxyMs },
  { scope: 'mcp_tool', title: 'Agent tool call latency', target: PERF_TARGETS.mcpToolMs },
] as const;

export function RequestCharts(): React.ReactElement {
  const context = useHistory();
  const p95 = useSeries('hotpath_p95', context.span);
  const requests = useSeries('hotpath_requests', context.span);
  const errors = useSeries('hotpath_server_errors', context.span);
  return (
    <>
      {FAMILIES.map((family) => (
        <SeriesChart
          key={family.scope}
          testId={`history-latency-${family.scope}`}
          results={[p95]}
          toPoints={([s]) => ({
            points: scopePeak(s!, family.scope),
            reference: { value: family.target, label: `${family.target} ms target` },
          })}
          presentation={{
            title: family.title,
            caption: 'Worst per-minute p95 in each interval',
            format: ms,
          }}
          context={context}
        />
      ))}
      <SeriesChart
        testId="history-errors"
        results={[requests, errors]}
        toPoints={([r, e]) => ({ points: errorRate(r!, e!) })}
        presentation={{
          title: 'Server errors',
          caption: 'Server errors as a share of requests; no requests reads as a gap',
          format: rate,
        }}
        context={context}
      />
    </>
  );
}

export function DatabaseCharts(): React.ReactElement {
  const context = useHistory();
  const connections = useSeries('postgres_connections', context.span);
  return (
    <>
      <SeriesChart
        testId="history-db-control"
        results={[connections]}
        toPoints={([s]) => ({ points: scopePercent(s!, 'control'), domainMax: 100 })}
        presentation={{
          title: 'Control database connections',
          caption: '% of its maximum',
          format: percent,
        }}
        context={context}
      />
      <SeriesChart
        testId="history-db-apps"
        results={[connections]}
        toPoints={([s]) => ({ points: scopePercent(s!, 'apps'), domainMax: 100 })}
        presentation={{
          title: 'Apps database connections',
          caption: '% of its maximum',
          format: percent,
          tone: 'apps',
        }}
        context={context}
      />
    </>
  );
}
